import { BrandMark, Cluster, Container, Stack } from "@humaniti/ui";
import { Link } from "react-router-dom";

/**
 * Each footer link is either internal to the marketing SPA (router push via
 * <Link>) or cross-SPA (real anchor — react-router would 404 inside marketing).
 * We mark the cross-SPA ones with `external: true`. No cross-SPA links exist
 * in the public footer right now (the Sign in column was dropped pre-launch
 * — see TopNav.tsx + ComingSoonPage.tsx for the rationale) but the type
 * stays so future external links land in the right shape.
 */
type FooterLink = { label: string; href: string; external?: boolean };

/**
 * Marketing footer — spec §6.2.
 *
 * | Column 1 — Network | Column 2 — Future | Column 3 — Trust |
 *
 * Brand mark + tagline ("A network for verified human-grade datasets.")
 * lives left of the columns. Version line below. The "Sign in" column was
 * removed pre-launch — until M1.6 the requester + annotator SPA sign-ins
 * are not advertised publicly. Staff sign in via /admin (URL not listed).
 */
export const Footer = () => (
  <footer
    style={{
      borderTop: "var(--border-1)",
      background: "var(--white)",
      padding: "var(--space-7) 0",
      marginTop: "var(--space-9)",
    }}
  >
    <Container size="full">
      <Cluster justify="between" align="start" gap="2xl">
        <Stack gap="md" style={{ maxWidth: 280 }}>
          <BrandMark variant="full" size="md" />
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-small)",
              color: "var(--fg-secondary)",
            }}
          >
            A network for verified human-grade datasets.
          </p>
        </Stack>

        <FooterColumn
          label="Network"
          links={[
            { label: "Humans", href: "/for-humans" },
            { label: "Builders", href: "/for-builders" },
            { label: "Pricing", href: "/pricing" },
            { label: "How it works", href: "/how-it-works" },
          ]}
        />

        <FooterColumn
          label="Future"
          links={[
            { label: "Roadmap", href: "/roadmap" },
            { label: "Manifesto", href: "/manifesto" },
            { label: "Field notes", href: "/notes" },
          ]}
        />

        <FooterColumn
          label="Trust"
          links={[
            { label: "Trust", href: "/trust" },
            { label: "Brand & Press", href: "/brand" },
            { label: "Privacy", href: "/privacy" },
            { label: "Terms", href: "/terms" },
            { label: "Cookies", href: "/cookies" },
            { label: "Acceptable use", href: "/acceptable-use" },
          ]}
        />
      </Cluster>

      <p
        style={{
          marginTop: "var(--space-7)",
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-label)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-muted)",
        }}
      >
        § humaniti / v0.0.1 — built with intent
      </p>
    </Container>
  </footer>
);

const linkStyle = { fontSize: "var(--fs-small)", color: "var(--fg-primary)" } as const;

const FooterColumn = ({ label, links }: { label: string; links: FooterLink[] }) => (
  <Stack gap="md">
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: "var(--fs-label)",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "var(--fg-secondary)",
      }}
    >
      {label}
    </span>
    {links.map((l) =>
      l.external ? (
        <a key={l.href} href={l.href} style={linkStyle}>
          {l.label}
        </a>
      ) : (
        <Link key={l.href} to={l.href} style={linkStyle}>
          {l.label}
        </Link>
      )
    )}
  </Stack>
);
